/**
 * applyListState - Filter+Sort Engine für Listen
 * 
 * Wendet Filter, Suche und Sortierung auf eine Liste an.
 * Client-side, für Stammdatenlisten.
 */

import type { ListState, ListConfig, TriStateFilter } from './types';

// German locale comparator
const deCollator = new Intl.Collator('de-DE', { sensitivity: 'base' });

/**
 * Prüft ob ein Wert "present" ist (nicht null/undefined/leer)
 */
export function isPresent(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

/**
 * Prüft ob ein Wert "missing" ist
 */
export function isMissing(value: unknown): boolean {
  return !isPresent(value);
}

/**
 * TriState Filter Helper
 */
export function matchesTriState(value: unknown, filter: TriStateFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'present') return isPresent(value);
  if (filter === 'missing') return isMissing(value);
  return true;
}

/**
 * Normalisiert Text für Suche (lowercase, trimmed)
 */
function normalizeSearchText(text: string): string {
  return text.toLowerCase().trim();
}

/**
 * Prüft ob Suchtext im Item gefunden wird
 */
function matchesSearch(searchTexts: string[], query: string): boolean {
  if (!query) return true;
  const normalizedQuery = normalizeSearchText(query);
  return searchTexts.some(text => 
    normalizeSearchText(text).includes(normalizedQuery)
  );
}

/**
 * Wendet ListState auf Items an und gibt gefilterte+sortierte Liste zurück
 */
export function applyListState<
  TItem,
  TFilters extends object,
  TSortKey extends string
>(
  items: TItem[],
  config: ListConfig<TItem, TFilters, TSortKey>,
  state: ListState<TFilters, TSortKey>
): TItem[] {
  let result = [...items];

  // 1. Status Filter
  if (state.status === 'active') {
    result = result.filter(item => config.getStatus(item) === true);
  } else if (state.status === 'inactive') {
    result = result.filter(item => config.getStatus(item) === false);
  }

  // 2. Custom Filters
  for (const [key, filterDef] of Object.entries(config.filterDefs)) {
    const filterValue = state.filters[key as keyof TFilters];
    if (filterValue === undefined || filterValue === null) continue;
    
    // Skip default/empty values
    if (Array.isArray(filterValue) && filterValue.length === 0) continue;
    if (filterValue === 'all') continue;
    
    const fn = filterDef as (item: TItem, value: unknown) => boolean;
    result = result.filter(item => fn(item, filterValue));
  }

  // 3. Search (Volltext)
  if (state.q) {
    result = result.filter(item => {
      const searchTextResult = config.getSearchText(item);
      const searchTexts = Array.isArray(searchTextResult) 
        ? searchTextResult 
        : [searchTextResult];
      return matchesSearch(searchTexts, state.q);
    });
  }

  // 4. Sort
  const sortDef = config.sortDefs[state.sortKey];
  if (sortDef) {
    result.sort((a, b) => {
      const cmp = sortDef(a, b);
      return state.sortDir === 'desc' ? -cmp : cmp;
    });
  }

  return result;
}

/**
 * Helper: String-Comparator (locale-aware)
 */
export function compareString(a: string | null | undefined, b: string | null | undefined): number {
  const aStr = a ?? '';
  const bStr = b ?? '';
  return deCollator.compare(aStr, bStr);
}

/**
 * Helper: Numeric-Comparator
 */
export function compareNumber(a: number | null | undefined, b: number | null | undefined): number {
  const aNum = a ?? 0;
  const bNum = b ?? 0;
  return aNum - bNum;
}

/**
 * Helper: Boolean-Comparator (true first)
 */
export function compareBoolean(a: boolean | null | undefined, b: boolean | null | undefined): number {
  const aVal = a === true ? 1 : 0;
  const bVal = b === true ? 1 : 0;
  return bVal - aVal; // true first
}

/**
 * Helper: Present/Missing Comparator (present first)
 */
export function comparePresence(a: unknown, b: unknown): number {
  const aPresent = isPresent(a) ? 1 : 0;
  const bPresent = isPresent(b) ? 1 : 0;
  return bPresent - aPresent; // present first
}

/**
 * Helper: Smart String/Number Comparator
 * Wenn beide Werte rein numerisch sind, numerisch vergleichen
 */
export function compareSmartNumeric(a: string | null | undefined, b: string | null | undefined): number {
  const aStr = a ?? '';
  const bStr = b ?? '';
  
  // Prüfe ob beide rein numerisch
  const aIsNumeric = /^\d+$/.test(aStr);
  const bIsNumeric = /^\d+$/.test(bStr);
  
  if (aIsNumeric && bIsNumeric) {
    return parseInt(aStr, 10) - parseInt(bStr, 10);
  }
  
  return deCollator.compare(aStr, bStr);
}
